import { ApolloError } from '@apollo/client';
import { ErrorRetry, useTwxEnabled } from '@bechtel/client-common';
import { StatBlock } from '@bechtel/client-dashboard-common';
import { drawerValues as defaultValues, DrawerSearchScope, WeldDrawerValuesProps } from '@bechtel/client-welds';
import {
  CommodityComplexFilterInput,
  CommodityFilterByTerm,
  CommodityFilterInput,
  CommodityFilterStatusFlag,
  CommodityStats,
  CommodityStatusCode,
  CrewMember,
  FilterOperator,
  PersonaType,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { filter, find, get, includes } from 'lodash';
import { FC, Fragment, useEffect } from 'react';
import { MultiStatBlock } from '~/components/organisms/multi-stat-block';
import { PriorityStatBlock } from '~/components/organisms/priority-stat-block';
import {
  useAssignToMeStatsFWEQuery,
  useCommodityAllPriorityStats,
  useCommodityBlocksStatsFWE,
  useCommodityStatsClaimedAndUnassignedFWEQuery,
  useCommodityStatsPriorityQuery,
  useCommodityStatsQuery,
  useCommodityStatsRejectedFWE,
} from '~/hooks';
import { WeldFilterInput } from '~/search-bar/types';

interface NavigateToWeldProps {
  weldDrawerValuesProps: WeldDrawerValuesProps;
  applyBlockedFilter?: string;
  isWeeklyStats?: boolean;
}
interface CommodityStatsSectionProps {
  personaType?: PersonaType;
  crewMember: CrewMember;
  navigateToWeld: (navigateToWeldProps: NavigateToWeldProps) => void;
  weldTypeId: string | undefined;
  fitupStatusStats?: CommodityStats[];
  loading?: boolean;
  error?: ApolloError | undefined;
  refetch?: (input: CommodityFilterInput | undefined) => void;
  weeklyStatusStats?: CommodityStats[];
  weldFilter?: WeldFilterInput;
}

export const CommodityStatsSection: FC<CommodityStatsSectionProps> = ({
  personaType,
  crewMember,
  navigateToWeld,
  weldTypeId,
  fitupStatusStats,
  loading,
  error,
  refetch,
  weeklyStatusStats,
  weldFilter,
}) => {
  const [
    getCommodityStats,
    {
      error: commodityStatsError,
      data: commodityStatsData,
      loading: commodityStatsLoading,
      refetch: commodityStatsRefetch,
    },
  ] = useCommodityStatsQuery(true);
  const { twxEnabled } = useTwxEnabled();
  const [
    getCommodityPriorityStats,
    {
      error: commodityPriorityStatsError,
      data: commodityPriorityStatsData,
      loading: commodityPriorityStatsLoading,
      refetch: commodityPriorityStatsRefetch,
    },
  ] = useCommodityStatsPriorityQuery(true);
  const [
    getCommodityStatsClaimedAndUnassignedFWE,
    {
      error: claimedUnassignedFWEError,
      data: claimedUnassignedFWEData,
      loading: claimedUnassignedFWELoading,
      refetch: claimedUnassignedFWERefetch,
    },
  ] = useCommodityStatsClaimedAndUnassignedFWEQuery(true);
  const [
    getCommodityStatsRejectedFWE,
    { error: rejectedFWEError, data: rejectedFWEData, loading: rejectedFWELoading, refetch: rejectedFWERefetch },
  ] = useCommodityStatsRejectedFWE(true);

  const [
    getCommodityBlocksStatsFWE,
    { error: blocksStatsError, data: blocksStatsData, loading: blocksStatsLoading, refetch: blocksStatsRefetch },
  ] = useCommodityBlocksStatsFWE(true);
  const [
    getAllPriorityStats,
    {
      error: allPriorityStatsError,
      data: allPriorityStatsData,
      loading: allPriorityStatsLoading,
      refetch: allPriorityStatsRefetch,
    },
  ] = useCommodityAllPriorityStats(true);
  const [
    getAssignToMeStatsFWE,
    {
      error: assignToMeStatsFWEError,
      data: assignToMeStatsFWEData,
      loading: assignToMeStatsFWELoading,
      refetch: assignToMeStatsFWERefetch,
    },
  ] = useAssignToMeStatsFWEQuery(true);

  useEffect(() => {
    if (!crewMember?.persona || !crewMember?.id || crewMember?.persona === PersonaType.SUPER_ADMIN || !weldTypeId) {
      return;
    }

    const complexFilterInput: CommodityComplexFilterInput = {
      and: [
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
          value: crewMember.id,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
      ],
    };
    if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PERSONA_TYPE_ID,
        value: crewMember?.persona,
        operator: FilterOperator.EQUALS,
      });
      const complexFilterInputWithoutSupportFM = JSON.parse(JSON.stringify(complexFilterInput));
      complexFilterInputWithoutSupportFM.and?.push(
        {
          term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
          value: 'false',
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          value: CommodityStatusCode.BLOCKED,
          operator: FilterOperator.EQUALS,
        }
      );

      // Add CREW_MEMBER_ID filter if foremen are selected from dropdown
      if (weldFilter?.searchByForeman?.length > 0) {
        complexFilterInputWithoutSupportFM.and?.push({
          term: CommodityFilterByTerm.CREW_MEMBER_ID,
          values: [...weldFilter.searchByForeman],
          operator: FilterOperator.IN,
        });
      }

      let complexFilterInputBlockStatsFWE = JSON.parse(JSON.stringify(complexFilterInputWithoutSupportFM));
      complexFilterInputBlockStatsFWE = filter(
        complexFilterInputBlockStatsFWE.and,
        (cft) => !includes([CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY], cft.term)
      );
      complexFilterInputBlockStatsFWE = { and: complexFilterInputBlockStatsFWE };
      const complexFilterInputAssignToMeFWE = JSON.parse(JSON.stringify(complexFilterInput));
      complexFilterInputAssignToMeFWE.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          values: [CommodityStatusCode.CLAIMED, CommodityStatusCode.VT_SENT_BACK],
          operator: FilterOperator.IN,
        },
        {
          term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
          value: 'false',
          operator: FilterOperator.EQUALS,
        }
      );
      getAssignToMeStatsFWE({
        variables: {
          input: {
            complexFilter: complexFilterInputAssignToMeFWE,
          },
        },
      });
      getCommodityBlocksStatsFWE({
        variables: {
          input: {
            complexFilter: complexFilterInputBlockStatsFWE,
          },
        },
      });
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
      const complexFilterInputAllProrityStats: CommodityComplexFilterInput = {
        and: [
          {
            term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
            value: weldTypeId,
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
            value: crewMember.id,
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.PERSONA_TYPE_ID,
            value: crewMember?.persona,
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.PRIORITY_WELDS,
            value: 'true',
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
            value: 'true',
            operator: FilterOperator.EQUALS,
          },
        ],
      };
      getAllPriorityStats({
        variables: {
          input: {
            complexFilter: complexFilterInputAllProrityStats,
          },
        },
      });
      const commodityComplexFilterInput: CommodityComplexFilterInput = {
        and: [
          {
            term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
            value: weldTypeId,
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
            value: crewMember.id,
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.PERSONA_TYPE_ID,
            value: crewMember?.persona,
            operator: FilterOperator.EQUALS,
          },
          {
            term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
            value: 'true',
            operator: FilterOperator.EQUALS,
          },
        ],
      };
      const commodityComplexFilterInputStatsCalimedAndUnassignedFWE = JSON.parse(
        JSON.stringify(commodityComplexFilterInput)
      );

      commodityComplexFilterInputStatsCalimedAndUnassignedFWE.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          value: CommodityStatusCode.CLAIMED,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.UNASSIGNED_COMMODITIES,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
          value: 'true',
          operator: FilterOperator.EQUALS,
        }
      );

      getCommodityStatsClaimedAndUnassignedFWE({
        variables: {
          input: {
            complexFilter: commodityComplexFilterInputStatsCalimedAndUnassignedFWE,
          },
        },
      });
      const commodityComplexFilterInputRejectedFWE = JSON.parse(JSON.stringify(commodityComplexFilterInput));
      commodityComplexFilterInputRejectedFWE.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          value: CommodityStatusCode.VT_SENT_BACK,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        }
      );
      getCommodityStatsRejectedFWE({
        variables: {
          input: {
            complexFilter: commodityComplexFilterInputRejectedFWE,
          },
        },
      });
    }
    const complexFilterInputStats = JSON.parse(JSON.stringify(complexFilterInput));
    const complexFilterInputPriorityStats = JSON.parse(JSON.stringify(complexFilterInput));

    if (personaType !== PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInputPriorityStats.and?.push({
        term: CommodityFilterByTerm.PRIORITY_WELDS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
      getCommodityPriorityStats({
        variables: {
          input: {
            complexFilter: complexFilterInputPriorityStats,
          },
        },
      });
      complexFilterInputStats.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [
          CommodityStatusCode.FITUP_INSPECTION,
          CommodityStatusCode.READY_TO_WELD,
          CommodityStatusCode.VT_SENT_BACK,
          CommodityStatusCode.BLOCKED,
        ],
        operator: FilterOperator.IN,
      });
      getCommodityStats({
        variables: {
          input: {
            complexFilter: complexFilterInputStats,
          },
        },
      });
    }
  }, [
    crewMember?.persona,
    crewMember?.id,
    getCommodityStats,
    weldTypeId,
    getCommodityBlocksStatsFWE,
    personaType,
    weldFilter,
  ]);

  const statusStats = get(commodityStatsData?.commodityStats, 'commodityStats.statusStats');
  const statusStatsRejectedFWEData = get(rejectedFWEData?.commodityStats, 'commodityStats.statusStats');
  const numPriorityCount = get(commodityPriorityStatsData?.commodityStats, 'commodityStats.numPriorityWelds', 0);
  const numAllPriorityCounts = get(allPriorityStatsData?.commodityStats, 'commodityStats.numPriorityWelds', 0);
  const numWelders = get(commodityStatsData?.commodityStats, 'commodityStats.numWelders', 0);
  const claimedUnassigned = get(claimedUnassignedFWEData?.commodityStats, 'commodityStats.numUnassigned', 0);
  const numCrews = get(commodityStatsData?.commodityStats, 'commodityStats.numCrews', 0);
  const numAssignedToMe = get(assignToMeStatsFWEData?.commodityStats, 'commodityStats.numAssignedToMe', 0);
  const statsWithoutSupportingFM = get(blocksStatsData?.commodityStats, 'commodityStats.statusStats');
  const getStatsWithoutSupportingFM = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(statsWithoutSupportingFM, (statusStat) => statusStat.status === statusFlag),
        'stats'
      ) || 0
    );
  };

  const getStatsRejectedFWE = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(statusStatsRejectedFWEData, (statusStat) => statusStat.status === statusFlag),
        'stats'
      ) || 0
    );
  };
  const getStats = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(statusStats, (statusStat) => statusStat.status === statusFlag),
        'stats'
      ) || 0
    );
  };

  const getWeeklyStats = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(weeklyStatusStats, (statusStat) => statusStat.status === statusFlag),
        'stats'
      ) || 0
    );
  };

  const getFitupStats = (statusFlag: CommodityFilterStatusFlag) => {
    return (
      get(
        find(fitupStatusStats, (statusStat) => statusStat?.status === statusFlag),
        'stats'
      ) || 0
    );
  };
  const numFitupInspectionFWE = getFitupStats(CommodityFilterStatusFlag.FITUP_INSPECTION);
  const numFitupInspection = getStats(CommodityFilterStatusFlag.FITUP_INSPECTION);
  const numReadyToWeld = getStats(CommodityFilterStatusFlag.READY_TO_WELD);
  const numApproved = getWeeklyStats(CommodityFilterStatusFlag.VT_APPROVED);
  const numBlocked = getStats(CommodityFilterStatusFlag.BLOCKED);
  const numRejected = getStats(CommodityFilterStatusFlag.VT_SENT_BACK);
  const numRejectedFWE = getStatsRejectedFWE(CommodityFilterStatusFlag.VT_SENT_BACK);
  const numClaimed = getWeeklyStats(CommodityFilterStatusFlag.CLAIMED);

  const numBlockedWithoutSupportingFM = getStatsWithoutSupportingFM(CommodityFilterStatusFlag.BLOCKED);
  const showError =
    commodityStatsError ||
    claimedUnassignedFWEError ||
    error ||
    blocksStatsError ||
    allPriorityStatsError ||
    assignToMeStatsFWEError ||
    rejectedFWEError ||
    commodityPriorityStatsError;
  const showLoader =
    !commodityStatsLoading ||
    !claimedUnassignedFWELoading ||
    !loading ||
    !blocksStatsLoading ||
    !allPriorityStatsLoading ||
    !assignToMeStatsFWELoading ||
    !rejectedFWELoading ||
    !commodityPriorityStatsLoading;
  const showRefetch = commodityStatsError
    ? commodityStatsRefetch
    : error
      ? refetch
      : blocksStatsError
        ? blocksStatsRefetch
        : allPriorityStatsError
          ? allPriorityStatsRefetch
          : claimedUnassignedFWEError
            ? claimedUnassignedFWERefetch
            : rejectedFWEError
              ? rejectedFWERefetch
              : commodityPriorityStatsError
                ? commodityPriorityStatsRefetch
                : assignToMeStatsFWERefetch;
  return (
    <Fragment>
      {showLoader && showError && (
        <div data-testid="retry">
          <ErrorRetry refetch={showRefetch}>
            <Trans> There was a problem loading commodity stats. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {personaType == PersonaType.FIELD_WELD_ENGINEER && (
        <StatBlock
          data-testid="claimed-unassigned-statBlock"
          label={t`Claimed & Unassigned`}
          value={claimedUnassigned}
          loading={loading}
          style={{
            cursor: claimedUnassigned !== 0 ? `pointer` : `default`,
          }}
          onClick={() => {
            if (claimedUnassigned === 0) return;
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList: [CommodityFilterStatusFlag.CLAIMED],
                searchScope: DrawerSearchScope.UNASSIGNED,
                isSupportingForeman: true,
              },
              applyBlockedFilter: 'true',
            };
            navigateToWeld(navigateToWeldProps);
          }}
        />
      )}
      {personaType == PersonaType.SUPERINTENDENT && (
        <MultiStatBlock
          loading={loading}
          data-testid="crew-statBlock"
          value={numCrews}
          valueText={t`Crews`}
          total={numWelders}
          totalText={t`Welders`}
          icon="users"
        />
      )}

      {personaType == PersonaType.FOREMAN && (
        <StatBlock
          loading={loading}
          data-testid="welders-statBlock"
          value={numWelders}
          label={t`Welders`}
          icon="users"
        />
      )}

      {!twxEnabled && (
        <StatBlock
          loading={personaType === PersonaType.FIELD_WELD_ENGINEER ? loading : commodityStatsLoading}
          data-testid="fitup-inspection-statBlock"
          value={personaType === PersonaType.FIELD_WELD_ENGINEER ? numFitupInspectionFWE : numFitupInspection}
          style={{ cursor: numFitupInspectionFWE + numFitupInspection == 0 ? `default` : `pointer` }}
          label={t`Fitup Inspection`}
          icon="clipboard-check"
          onClick={() => {
            if (numFitupInspectionFWE + numFitupInspection == 0) return;
            if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
              const navigateToWeldProps: NavigateToWeldProps = {
                weldDrawerValuesProps: {
                  ...defaultValues,
                  searchScope: DrawerSearchScope.ALL,
                  weldStatusList: [CommodityFilterStatusFlag.FITUP_INSPECTION],
                  isSupportingForeman: true,
                },
                applyBlockedFilter: 'true',
              };

              navigateToWeld(navigateToWeldProps);
            } else {
              const navigateToWeldProps: NavigateToWeldProps = {
                weldDrawerValuesProps: {
                  ...defaultValues,
                  searchScope: DrawerSearchScope.ASSIGNED_TO_ME,
                  weldStatusList: [CommodityFilterStatusFlag.FITUP_INSPECTION],
                },
                applyBlockedFilter: 'true',
              };
              navigateToWeld(navigateToWeldProps);
            }
          }}
        />
      )}
      {personaType !== PersonaType.FIELD_WELD_ENGINEER && (
        <PriorityStatBlock
          data-testid="ready-to-weld-statBlock"
          loading={loading}
          label={t`Ready to weld`}
          value={numReadyToWeld + numRejected}
          priorityCount={numPriorityCount}
          onStatClick={() => {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList:
                  numReadyToWeld + numRejected > 0
                    ? [CommodityFilterStatusFlag.READY_TO_WELD, CommodityFilterStatusFlag.VT_SENT_BACK]
                    : [],
              },
              applyBlockedFilter: 'true',
            };
            navigateToWeld(navigateToWeldProps);
          }}
          onPriorityStatClick={() => {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                isPriority: numPriorityCount > 0,
              },
            };
            navigateToWeld(navigateToWeldProps);
          }}
        ></PriorityStatBlock>
      )}
      {personaType === PersonaType.FIELD_WELD_ENGINEER && (
        <PriorityStatBlock
          loading={loading}
          data-testid="assigned-to-me-statBlock"
          label={t`Assigned to Me`}
          value={numAssignedToMe}
          style={{ cursor: numAssignedToMe !== 0 ? `pointer` : `default` }}
          priorityCount={numAllPriorityCounts}
          onStatClick={() => {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                searchScope: DrawerSearchScope.ASSIGNED_TO_ME,
                weldStatusList: [CommodityFilterStatusFlag.CLAIMED, CommodityFilterStatusFlag.VT_SENT_BACK],
                isSupportingForeman: false,
              },
              applyBlockedFilter: 'true',
            };
            navigateToWeld(navigateToWeldProps);
          }}
          onPriorityStatClick={() => {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                searchScope: DrawerSearchScope.ALL,
                isPriority: numAllPriorityCounts > 0,
                isSupportingForeman: true,
              },
            };
            navigateToWeld(navigateToWeldProps);
          }}
        ></PriorityStatBlock>
      )}
      {personaType === PersonaType.FIELD_WELD_ENGINEER && (
        <StatBlock
          loading={loading}
          data-testid="vt-approved-statBlock"
          label={t`VT Approved`}
          subLabel={t`This Week`}
          value={numApproved}
          style={{ cursor: numApproved !== 0 ? `pointer` : `default` }}
          onClick={() => {
            if (numApproved == 0) return;
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList: [CommodityFilterStatusFlag.VT_APPROVED],
                isSupportingForeman: true,
              },
              isWeeklyStats: true,
            };
            navigateToWeld(navigateToWeldProps);
          }}
        />
      )}
      <StatBlock
        loading={loading}
        data-testid="blocks-statBlock"
        label={t`Blocks`}
        value={personaType === PersonaType.FIELD_WELD_ENGINEER ? numBlockedWithoutSupportingFM : numBlocked}
        style={{ cursor: numBlockedWithoutSupportingFM + numBlocked !== 0 ? `pointer` : `default` }}
        icon="exclamation-triangle"
        onClick={() => {
          if (numBlocked + numBlockedWithoutSupportingFM == 0) return;
          if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList: [CommodityFilterStatusFlag.BLOCKED],
                isSupportingForeman: false,
              },
            };
            navigateToWeld(navigateToWeldProps);
          } else {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList: [CommodityFilterStatusFlag.BLOCKED],
              },
            };
            navigateToWeld(navigateToWeldProps);
          }
        }}
      />
      <StatBlock
        loading={loading}
        data-testid="vt-sent-back-statBlock"
        label={t`VT Sent Back`}
        value={personaType === PersonaType.FIELD_WELD_ENGINEER ? numRejectedFWE : numRejected}
        style={{ cursor: numRejected + numRejectedFWE !== 0 ? `pointer` : `default` }}
        icon="ban"
        iconFillColor="purple.600"
        onClick={() => {
          if (numRejected + numRejectedFWE == 0) return;
          if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList: [CommodityFilterStatusFlag.VT_SENT_BACK],
                isSupportingForeman: false,
              },
              applyBlockedFilter: 'true',
            };
            navigateToWeld(navigateToWeldProps);
          } else {
            const navigateToWeldProps: NavigateToWeldProps = {
              weldDrawerValuesProps: {
                ...defaultValues,
                weldStatusList: [CommodityFilterStatusFlag.VT_SENT_BACK],
              },
              applyBlockedFilter: 'true',
            };
            navigateToWeld(navigateToWeldProps);
          }
        }}
      />
      {personaType !== PersonaType.FIELD_WELD_ENGINEER && (
        <>
          <StatBlock
            loading={loading}
            data-testid="claimed-statBlock"
            label={t`Claimed`}
            subLabel={t`This Week`}
            value={numClaimed}
            style={{ cursor: numClaimed !== 0 ? `pointer` : `default` }}
            onClick={() => {
              if (numClaimed == 0) return;
              const navigateToWeldProps: NavigateToWeldProps = {
                weldDrawerValuesProps: {
                  ...defaultValues,
                  weldStatusList: [CommodityFilterStatusFlag.CLAIMED],
                },
                isWeeklyStats: true,
              };
              navigateToWeld(navigateToWeldProps);
            }}
          />

          <StatBlock
            loading={loading}
            data-testid="vt-approved-statBlock"
            label={t`VT Approved`}
            subLabel={t`This Week`}
            value={numApproved}
            style={{ cursor: numApproved !== 0 ? `pointer` : `default` }}
            onClick={() => {
              if (numApproved == 0) return;
              const navigateToWeldProps: NavigateToWeldProps = {
                weldDrawerValuesProps: {
                  ...defaultValues,
                  weldStatusList: [CommodityFilterStatusFlag.VT_APPROVED],
                },
                isWeeklyStats: true,
              };
              navigateToWeld(navigateToWeldProps);
            }}
          />
        </>
      )}
    </Fragment>
  );
};
